import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { logoutUser } from "../store/slices/authSlice";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Footer from "./Footer";
import { useFooter } from "../contexts/FooterContext";

/** Matches `Footer` fixed bar (~40.8px) plus a small gap so content is not covered. */
const FIXED_FOOTER_RESERVE_PX = 48;

/**
 * Main dashboard layout component with sidebar and header
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render in main content area
 */
const DashboardLayout = ({ children }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { isFooterVisible } = useFooter();
  useEffect(() => {
    if (!user) {
      window.location.href = "/login";
    }
  }, [user]);
  const location = useLocation();

  /**
   * Get current page name based on the current route
   */
  const getCurrentPageName = () => {
    const path = location.pathname;
    const routeMap = {
      "/test": { heading: "Test", subheading: "Test" },
      "/omni-watch": {
        heading: "Oomni Watch",
        subheading: "Cameras",
      },
      "/live-monitoring": {
        heading: "Live Monitoring",
        subheading: "Vision AI detections",
      },
      "/ai-analysis": {
        heading: "AI Analysis",
        subheading: "Alert rules & history",
      },
      "/flashback": {
        heading: "Flashback",
        subheading: "Live & recorded cameras",
      },
      "/administration": {
        heading: "Administration",
        subheading: "System administration",
      },
      "/user-management/users": {
        heading: "Users",
        subheading: "User Management",
      },
      "/administration/camera-mgmt": {
        heading: "Administration",
        subheading: "Camera Management",
      },
      "/administration/zone-mgmt": {
        heading: "Administration",
        subheading: "Zone Management",
      },
      "/administration/ai-models-mgmt": {
        heading: "Administration",
        subheading: "AI Management",
      },
      "/administration/user-mgmt": {
        heading: "Administration",
        subheading: "User Mgmt",
      },
      "/administration/nvr-mgmt": {
        heading: "Administration",
        subheading: "NVR Management",
      },
    };

    if (routeMap[path]) {
      return routeMap[path];
    }

    if (path.startsWith("/cameras/") && path.endsWith("/playback")) {
      return { heading: "Flashback", subheading: "Camera playback" };
    }

    if (path.startsWith("/administration")) {
      return { heading: "Administration", subheading: "System administration" };
    }

    return {
      heading: "Oomni Watch",
      subheading: "Oomni Watch",
    };
  };

  /**
   * Handle user logout
   */
  const handleLogout = async () => {
    await dispatch(logoutUser());
    window.location.replace("/login");
  };

  /**
   * Toggle sidebar collapsed state
   */
  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const fillViewport =
    location.pathname.startsWith("/administration") ||
    location.pathname.startsWith("/omni-watch") ||
    location.pathname.startsWith("/live-monitoring") ||
    location.pathname.startsWith("/ai-analysis") ||
    location.pathname.startsWith("/flashback") ||
    (location.pathname.startsWith("/cameras/") &&
      location.pathname.endsWith("/playback"));

  return (
    <div>
      <div className="dashboard-layout">
        <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />

        <div
          className={`main-content ${sidebarCollapsed ? "sidebar-collapsed" : ""
            }`}
        >
          <Header
            user={user}
            onLogout={handleLogout}
            onToggleSidebar={toggleSidebar}
            currentPage={getCurrentPageName()}
          />

          <main
            className={`content-area${fillViewport ? " content-area--fill-viewport" : ""
              }`}
            style={{
              /*paddingBottom: isFooterVisible ? FIXED_FOOTER_RESERVE_PX : 0,
              */boxSizing: "border-box",
            }}
          >
            {children}
          </main>
        </div>
      </div>
      {isFooterVisible && <Footer />}
    </div>
  );
};

export default DashboardLayout;
